import { useState } from 'react'
import { ImageIcon, Search } from 'lucide-react'
import { useMedia } from '@/api/queries'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Field'
import { cn } from '@/lib/cn'
import type { MediaItem } from '@/types'
import { MediaThumb } from './MediaThumb'

/** A client keeps hundreds to thousands of images; the grid shows the first matches only. */
const SHOWN = 60

/**
 * Picks one image from the client's media library (PC-38): approved, usable
 * images only, as the API accepts. Inline rather than a second modal, so it
 * sits inside a form's modal.
 *
 * `current` is the stored pick as the server shows it: its id, and its URL
 * while it is still an approved, usable image (null once it is not). A pick
 * that is no longer shown is kept until it is changed, and says so.
 *
 * Uploading an image here waits on APP-138: there is no upload yet.
 */
export function ImagePicker({
  clientId,
  value,
  current,
  onChange,
}: {
  clientId: string
  value: string | null
  current: { id: string | null; url: string | null }
  onChange: (next: string | null) => void
}) {
  const media = useMedia(clientId)
  const [browsing, setBrowsing] = useState(false)
  const [query, setQuery] = useState('')

  const images = (media.data ?? []).filter(
    (m) => m.kind === 'image' && m.status === 'approved' && m.usable,
  )
  const picked = images.find((m) => m.id === value) ?? null
  const pickedUrl = picked?.url ?? (value !== null && value === current.id ? current.url : null)
  // The stored pick is no longer an approved, usable image: kept, but not shown anywhere.
  const hidden = value !== null && value === current.id && current.url === null

  const q = query.trim().toLowerCase()
  const matches = q ? images.filter((m) => matchText(m).includes(q)) : images

  const choose = (id: string) => {
    onChange(id)
    setBrowsing(false)
    setQuery('')
  }

  return (
    <div className="space-y-2">
      <p className="field-label">Image</p>
      {pickedUrl ? (
        <Card className="flex items-center gap-3 p-3">
          <img
            src={pickedUrl}
            alt=""
            referrerPolicy="no-referrer"
            className="h-16 w-24 shrink-0 rounded-[4px] border border-rule object-cover"
          />
          <p className="min-w-0 flex-1 truncate text-[13px]">
            {picked?.title ?? picked?.description ?? 'Picked image'}
          </p>
          <Button size="sm" variant="quiet" onClick={() => onChange(null)}>
            Remove
          </Button>
        </Card>
      ) : hidden ? (
        <div className="flex items-center gap-3">
          <p role="alert" className="flex-1 text-[12px] text-brand-red-700">
            This image is no longer approved, so it is not shown. Pick another or remove it.
          </p>
          <Button size="sm" variant="quiet" onClick={() => onChange(null)}>
            Remove
          </Button>
        </div>
      ) : (
        <p className="text-[12px] text-ink-faint">No image.</p>
      )}

      {browsing ? (
        <Card className="space-y-3 p-3">
          <div className="relative">
            <Search
              size={14}
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint"
            />
            <Input
              autoFocus
              aria-label="Search approved images"
              placeholder="Search by title or description"
              value={query}
              className="pl-8"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {media.isLoading ? (
            <p className="text-[12px] text-ink-faint">Loading the media library…</p>
          ) : images.length === 0 ? (
            <p className="text-[12px] text-ink-faint">
              Approve an image on the Media page to use it here.
            </p>
          ) : matches.length === 0 ? (
            <p className="text-[12px] text-ink-faint">No approved image matches.</p>
          ) : (
            <>
              <ul className="grid max-h-[320px] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
                {matches.slice(0, SHOWN).map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      aria-pressed={m.id === value}
                      aria-label={m.title ?? m.description ?? m.url}
                      onClick={() => choose(m.id)}
                      className={cn(
                        'block w-full rounded-[6px] border-2 p-0.5',
                        m.id === value
                          ? 'border-brand-blue'
                          : 'border-transparent hover:border-rule',
                      )}
                    >
                      <MediaThumb item={m} />
                    </button>
                  </li>
                ))}
              </ul>
              {matches.length > SHOWN && (
                <p className="text-[12px] text-ink-faint">
                  Showing {SHOWN} of {matches.length}. Search to narrow it down.
                </p>
              )}
            </>
          )}
          <div className="flex justify-end">
            <Button size="sm" variant="quiet" onClick={() => setBrowsing(false)}>
              Close
            </Button>
          </div>
        </Card>
      ) : (
        <Button size="sm" variant="quiet" onClick={() => setBrowsing(true)}>
          <ImageIcon size={12} aria-hidden />
          {value === null ? 'Choose from the media library' : 'Choose another'}
        </Button>
      )}
    </div>
  )
}

function matchText(m: MediaItem): string {
  return [m.title, m.description, m.url].filter(Boolean).join(' ').toLowerCase()
}
